// SiteActions — the site's ONE registry and its overlays (ADR-036 §7 — the four
// kinds; the exemplar is `pages/teams/TeamActions.tsx`).
//   • `siteActs` is the PURE registry — rows with a kind, a section, a gate and
//     a `show` predicate over the status and the closed-over facts; the shape
//     test (`components/patterns/actionGrammar.test.ts`) walks it.
//   • `useSiteActions` resolves it for a site and returns the ACTS every surface
//     composes from — the peek's top row + door through `ContextPanel` (ONE top
//     row for both faces), the page's seat + door through `EntityHeader`, each
//     Overview card's act through `SectionAct` — the OVERLAYS (the record
//     drawer, the two timeline flows' forms, the chains drawer, the dates
//     drawer, the lifecycle confirms), mounted ONCE by the caller, and the two
//     FLOWS' handles (the peek's inline history rows and reconcile body open
//     the same forms per row).
// The acts: NO primary at rest; a DRAFT's primary is Resume setup — the wizard
// IS the draft's page (the locked draft model). The edits, one per Overview
// card: Edit site (Identity — the record drawer) · Update classification
// (Classification — a CHOOSER, ADR-038 §1: the classification changed · correct
// data · correct start date; "Classify site" when never classified) · Update
// config (Solution — the same chooser over the solution timeline; only with a
// current config) · Manage responsibilities (the people key with the site
// level — `namer:site`; the chains drawer at its list) · Correct dates
// (Lifecycle — the recorded days, a drawer; only while a day is held). The
// LIFECYCLE: Activate (navigates to the wizard's review step — the user
// activates off the summary there; the wizard owns the SITE_INCOMPLETE /
// NO_SEATS friendly faces) · Suspend (a REQUIRED reason + the declared day) ·
// Resume (the day) · Decommission (the day; the danger act) · Delete draft
// (the only hard delete — working data; site:write). Each lifecycle day
// defaults to the SITE's today (ADR-082 §6), never the browser's; the server
// owns the ordering law. One string per act: the row's word is the card's link
// and the drawer's title.

import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SiteStatus } from '@ems-app/schemas/sites/requests';
import { LIFECYCLE_DAY_FIELDS } from '@ems-app/schemas/sites/requests';
import type { SiteDetail } from '@ems-app/schemas/sites/responses';
import { resolveActs, type ActionItem, type EntityAct } from '../../components/patterns/entityRegistry.js';
import { ChainsDrawer } from '../../components/patterns/ChainsDrawer.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { DatePicker } from '../../components/ui/DatePicker.js';
import { Label } from '../../components/ui/Label.js';
import { Textarea } from '../../components/ui/Textarea.js';
import { icons } from '../../components/ui/icons.js';
import { useToast } from '../../components/ui/Toast.js';
import { useCan } from '../../hooks/useCan.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';
import { useWeekStart } from '../../hooks/useWeekStart.js';
import { useDecommissionSite, useDeleteDraftSite, useResumeSite, useSuspendSite } from '../../hooks/useSites.js';
import { todayIn } from '../../lib/period.js';
import { EditSiteDrawer } from './EditSiteDrawer.js';
import { useDesignationsFlow, type DesignationOverlay } from './DesignationsFlow.js';
import { useSolutionFlow, type SolutionOverlay } from './SolutionFlow.js';
import { CorrectDatesDrawer } from './SiteLifecycle.js';

/** What each act does — the hook wires these to its overlays, flows and routes. */
export interface SiteActHandlers {
  edit: () => void;
  /** Never classified — the first-values PUT. */
  classify: () => void;
  designationsChanged: () => void;
  designationsCorrect: () => void;
  designationsStartDate: () => void;
  solutionChanged: () => void;
  solutionCorrect: () => void;
  solutionStartDate: () => void;
  manageResponsibilities: () => void;
  correctDates: () => void;
  activate: () => void;
  suspend: () => void;
  resume: () => void;
  decommission: () => void;
  deleteDraft: () => void;
}

/** The closed-over facts the rows read beside the status. */
export interface SiteActFacts {
  /** The setup wizard — a draft's primary navigates there. */
  setupRoute: string;
  /** A designation period exists — Update classification (the chooser); otherwise Classify site. */
  classified: boolean;
  /** A current solution config exists — Update config; the body's "Set up config" otherwise. */
  hasConfig: boolean;
  /** A lifecycle day is recorded — Correct dates may move it (a draft holds none). */
  heldDays: boolean;
}

/** The site's registry — pure: the rows, their kinds, sections and predicates. */
export function siteActs(h: SiteActHandlers, facts: SiteActFacts): ActionItem<SiteStatus>[] {
  const notDraft = (ctx: { status: SiteStatus }) => ctx.status !== 'draft';
  return [
    // The draft's primary — the wizard is its page; at rest the site has none (the seat is empty on Overview,
    // a tab's own while mounted — Import on Files).
    { id: 'resume-setup', kind: 'primary', labelKey: 'peek.resumeSetup', icon: icons.play, run: { route: facts.setupRoute }, gate: 'site:write', order: 10, show: (ctx) => ctx.status === 'draft' },
    // The edits — one per Overview card; the peek's Edit menu holds them all.
    { id: 'edit', kind: 'edit', section: 'identity', labelKey: 'actions.edit', icon: icons.edit, run: { handler: h.edit }, gate: 'site:write', order: 10, show: notDraft },
    facts.classified
      ? {
          id: 'designations',
          kind: 'edit',
          section: 'classification',
          labelKey: 'designations.update',
          icon: icons.siteType,
          gate: 'site:write',
          order: 20,
          show: notDraft,
          // The chooser (ADR-038 §1): a classification value is a DATED PERIOD, so the act asks what happened first.
          questionKey: 'designations.chooserTitle',
          choices: [
            { id: 'changed', labelKey: 'designations.menu.changed', helpKey: 'designations.menu.changedHelp', icon: icons.calendar, run: { handler: h.designationsChanged } },
            { id: 'correct', labelKey: 'reconcile.menu.correctData', helpKey: 'designations.menu.correctDataHelp', icon: icons.edit, run: { handler: h.designationsCorrect } },
            { id: 'start-date', labelKey: 'reconcile.menu.correctDate', helpKey: 'reconcile.menu.correctDateHelp', icon: icons.clock, run: { handler: h.designationsStartDate } },
          ],
        }
      : // Never classified (pre-SCD legacy) — one act: the first-values PUT.
        { id: 'designations', kind: 'edit', section: 'classification', labelKey: 'designations.classify', icon: icons.siteType, run: { handler: h.classify }, gate: 'site:write', order: 20, show: notDraft },
    {
      id: 'solution',
      kind: 'edit',
      section: 'solution',
      labelKey: 'reconcile.verbs.updateConfig',
      icon: icons.energy,
      gate: 'site.solution_config:write',
      order: 30,
      show: (ctx) => ctx.status !== 'draft' && facts.hasConfig,
      questionKey: 'reconcile.chooserTitle',
      choices: [
        { id: 'changed', labelKey: 'reconcile.menu.changed', helpKey: 'reconcile.menu.changedHelp', icon: icons.calendar, run: { handler: h.solutionChanged } },
        { id: 'correct', labelKey: 'reconcile.menu.correctData', helpKey: 'reconcile.menu.correctDataHelp', icon: icons.edit, run: { handler: h.solutionCorrect } },
        { id: 'start-date', labelKey: 'reconcile.menu.correctDate', helpKey: 'reconcile.menu.correctDateHelp', icon: icons.clock, run: { handler: h.solutionStartDate } },
      ],
    },
    // The chains' namer: the people key with the site level (`namer:site`); own-scope is satisfied by construction
    // (a visible site is within the viewer's reach) and the write re-checks authoritatively on save.
    { id: 'manage-responsibilities', kind: 'edit', section: 'responsibilities', labelKey: 'common:chains.manage', icon: icons.responsibilities, run: { handler: h.manageResponsibilities }, gate: 'namer:site', entitlement: 'has_field_force', order: 40 },
    // The recorded days' correction — only while a day is held (a draft holds none).
    { id: 'correct-dates', kind: 'edit', section: 'lifecycle', labelKey: 'lifecycle.correct.edit', icon: icons.calendar, run: { handler: h.correctDates }, gate: 'site:lifecycle', order: 50, show: () => facts.heldDays },
    // The lifecycle — the door; the exit verbs are the danger acts, fenced last. Gates per the catalogue:
    // the lifecycle verbs on site:lifecycle; delete-draft is a WRITE (a draft is working data).
    { id: 'activate', kind: 'lifecycle', labelKey: 'actions.activate', icon: icons.check, run: { handler: h.activate }, gate: 'site:lifecycle', order: 10, show: (ctx) => ctx.status === 'draft' },
    { id: 'suspend', kind: 'lifecycle', labelKey: 'actions.suspend', icon: icons.statusPaused, run: { handler: h.suspend }, gate: 'site:lifecycle', order: 10, show: (ctx) => ctx.status === 'active' },
    { id: 'resume', kind: 'lifecycle', labelKey: 'actions.resume', icon: icons.restore, run: { handler: h.resume }, gate: 'site:lifecycle', order: 10, show: (ctx) => ctx.status === 'suspended' },
    { id: 'decommission', kind: 'lifecycle', labelKey: 'actions.decommission', icon: icons.statusArchived, run: { handler: h.decommission }, gate: 'site:lifecycle', order: 20, tone: 'danger', show: (ctx) => ctx.status === 'active' || ctx.status === 'suspended' },
    { id: 'delete-draft', kind: 'lifecycle', labelKey: 'actions.deleteDraft', icon: icons.delete, run: { handler: h.deleteDraft }, gate: 'site:write', order: 20, tone: 'danger', show: (ctx) => ctx.status === 'draft' },
  ];
}

export interface UseSiteActionsOptions {
  /** After a decommission / a draft's delete (the peek closes, the page leaves). */
  onGone?: () => void;
}

export interface SiteActions {
  /** The resolved set — what the shells and the cards compose from. */
  acts: EntityAct[];
  /** Every dialog and drawer, mounted ONCE by the caller beside its surface. */
  overlays: ReactNode;
  /** The classification timeline's flow — the peek's inline history rows retarget its overlays. */
  designations: { openOverlay: (overlay: DesignationOverlay) => void; deleteBlocked: boolean };
  /** The solution timeline's flow — the peek's reconcile body opens its forms per row. */
  solution: { openOverlay: (overlay: SolutionOverlay) => void; deleteBlocked: boolean };
}

export function useSiteActions(projectId: string, site: SiteDetail, { onGone }: UseSiteActionsOptions = {}): SiteActions {
  const { t } = useTranslation('sites');
  const toast = useToast();
  const navigate = useNavigate();
  const can = useCan(projectId);
  const entitlements = useEntitlements(projectId);
  // "Today" for a lifecycle day is the SITE's day (ADR-082 §6 — never the browser's clock); a draft without a
  // zone reads the project's.
  const display = useDisplayZone({ kind: 'site', projectId, zone: site.timezone_iana });
  const zone = display.zone ?? display.viewerZone ?? 'UTC';
  const today = todayIn(zone);
  const weekStart = useWeekStart(projectId);
  const suspend = useSuspendSite(projectId);
  const resume = useResumeSite(projectId);
  const decommission = useDecommissionSite(projectId);
  const deleteDraft = useDeleteDraftSite(projectId);

  // The two timelines' flows — ONE overlay state each, shared by the act and the peek's own history rows.
  const designationsFlow = useDesignationsFlow({ projectId, site, siteName: site.name, status: site.status, timeZone: display.zone });
  const solutionFlow = useSolutionFlow({ projectId, site, timeZone: display.zone, status: site.status, suggestions: site.suggestions });

  const [editOpen, setEditOpen] = useState(false);
  const [chainsOpen, setChainsOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendedOn, setSuspendedOn] = useState<string | null>(today);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumedOn, setResumedOn] = useState<string | null>(today);
  const [decommissionOpen, setDecommissionOpen] = useState(false);
  const [decommissionedOn, setDecommissionedOn] = useState<string | null>(today);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const name = site.name;
  const currentDesignation = site.designation_history.find((p) => p.is_current) ?? null;
  const currentConfig = solutionFlow.current;

  const acts = resolveActs(
    siteActs(
      {
        edit: () => setEditOpen(true),
        classify: () => designationsFlow.openOverlay({ kind: 'form', mode: 'first', periodId: null }),
        designationsChanged: () => designationsFlow.openOverlay({ kind: 'form', mode: 'changed', periodId: null }),
        designationsCorrect: () => currentDesignation && designationsFlow.openOverlay({ kind: 'form', mode: 'correct', periodId: currentDesignation.id }),
        designationsStartDate: () => currentDesignation && designationsFlow.openOverlay({ kind: 'startDate', periodId: currentDesignation.id }),
        solutionChanged: () => solutionFlow.openOverlay({ kind: 'form', mode: 'changed', periodId: null }),
        solutionCorrect: () => currentConfig && solutionFlow.openOverlay({ kind: 'form', mode: 'correct', periodId: currentConfig.id }),
        solutionStartDate: () => currentConfig && solutionFlow.openOverlay({ kind: 'startDate', periodId: currentConfig.id }),
        // The chains drawer at its list — the card's ONE door (its rows are content).
        manageResponsibilities: () => setChainsOpen(true),
        correctDates: () => setDatesOpen(true),
        activate: () => navigate(`/p/${projectId}/sites/${site.id}/setup`, { state: { atStep: 'review' } }),
        suspend: () => { setSuspendReason(''); setSuspendedOn(today); setSuspendOpen(true); },
        resume: () => { setResumedOn(today); setResumeOpen(true); },
        decommission: () => { setDecommissionedOn(today); setDecommissionOpen(true); },
        deleteDraft: () => setDeleteOpen(true),
      },
      {
        setupRoute: `/p/${projectId}/sites/${site.id}/setup`,
        classified: site.designation_history.length > 0,
        hasConfig: currentConfig !== null,
        heldDays: LIFECYCLE_DAY_FIELDS.some((f) => site[f] !== null),
      },
    ),
    { can, entitlements, status: site.status },
    (key) => t(key),
  );

  const overlays = (
    <>
      {/* The record's drawer mounts only while OPEN — nothing of its lists loads for a closed drawer (ADR-080 §7). */}
      {editOpen && <EditSiteDrawer projectId={projectId} site={site} open={editOpen} onClose={() => setEditOpen(false)} />}
      {designationsFlow.overlays}
      {solutionFlow.overlays}
      {can('namer:site') && <ChainsDrawer key={site.id} projectId={projectId} scope={{ level: 'site', id: site.id, name: site.name, closed: site.status === 'decommissioned' }} open={chainsOpen} onClose={() => setChainsOpen(false)} />}
      {datesOpen && <CorrectDatesDrawer projectId={projectId} site={site} open={datesOpen} onClose={() => setDatesOpen(false)} />}

      {/* Suspend — an operational pause; the reason is REQUIRED (the wire schema). */}
      <ConfirmDialog
        open={suspendOpen}
        onClose={() => setSuspendOpen(false)}
        tone="danger"
        title={t('lifecycle.suspend.title', { name })}
        confirmLabel={t('lifecycle.suspend.confirm')}
        onConfirm={async () => {
          // The reason is REQUIRED (the wire schema) — an empty one keeps the
          // dialog open with the inline alert (ConfirmDialog's failure contract).
          if (suspendReason.trim().length === 0) throw new Error(t('lifecycle.suspend.reasonRequired'));
          if (suspendedOn === null) throw new Error(t('lifecycle.dateRequired'));
          await suspend.mutateAsync({ siteId: site.id, reason: suspendReason.trim(), suspendedOn });
          toast.success(t('lifecycle.suspend.done', { name }));
        }}
      >
        <div className="space-y-2">
          <p className="text-sm text-[var(--color-text-primary)]">{t('lifecycle.suspend.body')}</p>
          <div>
            <Label htmlFor="site-suspend-reason" required>
              {t('lifecycle.suspend.reason')}
            </Label>
            <Textarea
              id="site-suspend-reason"
              rows={2}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder={t('lifecycle.suspend.reasonPlaceholder')}
            />
          </div>
          <div className="max-w-xs">
            <DatePicker label={t('lifecycle.suspend.since')} value={suspendedOn} onChange={setSuspendedOn} maxValue={today} weekStart={weekStart} zone={zone} />
          </div>
        </div>
      </ConfirmDialog>

      {/* Resume — closes the pause as of a day (the pause stays on the record). */}
      <ConfirmDialog
        open={resumeOpen}
        onClose={() => setResumeOpen(false)}
        tone="default"
        title={t('lifecycle.resume.title', { name })}
        confirmLabel={t('lifecycle.resume.confirm')}
        onConfirm={async () => {
          if (resumedOn === null) throw new Error(t('lifecycle.dateRequired'));
          await resume.mutateAsync({ siteId: site.id, resumedOn });
          toast.success(t('lifecycle.resume.done', { name }));
        }}
      >
        <div className="space-y-2">
          <p className="text-sm text-[var(--color-text-primary)]">{t('lifecycle.resume.body')}</p>
          <div className="max-w-xs">
            <DatePicker label={t('lifecycle.resume.on')} value={resumedOn} onChange={setResumedOn} maxValue={today} weekStart={weekStart} zone={zone} />
          </div>
        </div>
      </ConfirmDialog>

      {/* Decommission — the exit verb (soft; the seat frees — ADR-055), as of a day. */}
      <ConfirmDialog
        open={decommissionOpen}
        onClose={() => setDecommissionOpen(false)}
        tone="danger"
        title={t('lifecycle.decommission.title', { name })}
        confirmLabel={t('lifecycle.decommission.confirm')}
        onConfirm={async () => {
          if (decommissionedOn === null) throw new Error(t('lifecycle.dateRequired'));
          await decommission.mutateAsync({ siteId: site.id, decommissionedOn });
          toast.success(t('lifecycle.decommission.done', { name }));
          onGone?.();
        }}
      >
        <div className="space-y-2">
          <p className="text-sm text-[var(--color-text-primary)]">{t('lifecycle.decommission.body')}</p>
          <div className="max-w-xs">
            <DatePicker label={t('lifecycle.decommission.on')} value={decommissionedOn} onChange={setDecommissionedOn} maxValue={today} weekStart={weekStart} zone={zone} />
          </div>
        </div>
      </ConfirmDialog>

      {/* Delete draft — the only hard delete (working data, not a lifecycle act). */}
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        tone="danger"
        title={t('lifecycle.deleteDraft.title', { name })}
        description={t('lifecycle.deleteDraft.body')}
        confirmLabel={t('lifecycle.deleteDraft.confirm')}
        onConfirm={async () => {
          await deleteDraft.mutateAsync({ siteId: site.id });
          toast.success(t('lifecycle.deleteDraft.done', { name }));
          onGone?.();
        }}
      />
    </>
  );

  return {
    acts,
    overlays,
    designations: { openOverlay: designationsFlow.openOverlay, deleteBlocked: designationsFlow.deleteBlocked },
    solution: { openOverlay: solutionFlow.openOverlay, deleteBlocked: solutionFlow.deleteBlocked },
  };
}
